import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { LanguageSelector } from '@/components/LanguageSelector';
import Banner from '@/components/feedback/Banner';
import { useAuth } from '@/contexts';
import { apiClient, authApi } from '@/lib/api';

interface SequenceItem {
  key: 'rental_agreement' | 'damage_waiver' | 'check_in_form';
  title: string;
  status: 'not_started' | 'in_progress' | 'completed';
}

interface SequenceData {
  reservation: {
    listingName: string;
    arrivalDate: string;
    departureDate: string;
  };
  order: string[];
  items: SequenceItem[];
  links: Record<string, string>;
  progress: {
    completed: number;
    total: number;
  };
}

function Sequence() {
  const { authedCode, refreshToken } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('portal');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sequenceData, setSequenceData] = useState<SequenceData | null>(null);

  // BES-7770: re-mint the token so newly added sequence steps are openable.
  const refreshScopes = useCallback(async () => {
    if (!authedCode) return;
    try {
      const { token } = await authApi.validateCode(authedCode, 'pre-arrival-checklist');
      refreshToken(token);
    } catch (err) {
      console.debug('sequence: token scope refresh failed; keeping current token', err);
    }
  }, [authedCode, refreshToken]);

  // Load sequence data from API
  useEffect(() => {
    const loadSequenceData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [data] = await Promise.all([
          apiClient.get('/sequence/ui') as Promise<SequenceData>,
          refreshScopes(),
        ]);
        setSequenceData(data);
      } catch (err) {
        console.error('Error loading sequence data:', err);
        setError(t('Failed to load sequence data'));
      } finally {
        setLoading(false);
      }
    };

    loadSequenceData();
  }, [t, refreshScopes]);

  // Set page title
  useEffect(() => {
    document.title = t('Complete Your Tasks');
  }, [t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">{t('Loading your tasks...')}</span>
      </div>
    );
  }

  if (error || !sequenceData) {
    return <Banner type="error">{error || t('Unable to load sequence data')}</Banner>;
  }

  const { items, progress } = sequenceData;
  const isAllCompleted = progress.completed === progress.total;

  // Create items lookup for ordered rendering
  const itemsByKey = items.reduce(
    (acc, item) => {
      acc[item.key] = item;
      return acc;
    },
    {} as Record<string, SequenceItem>
  );

  function handleTaskClick(taskKey: string) {
    const path =
      taskKey === 'rental_agreement' ? '/sign-agreement' : taskKey === 'damage_waiver' ? '/damage-waiver' : '/check-in';
    navigate(`${path}?return=${encodeURIComponent('/pre-arrival-checklist')}`);
  }

  return (
    <>
      <LanguageSelector />

      {/* Progress Bar */}
      <div className="mt-6 mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">{t('Progress')}</span>
          <span className="text-sm text-gray-500">
            {t('{{completed}} of {{total}} completed', {
              completed: progress.completed,
              total: progress.total,
            })}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="h-2 rounded-full transition-all duration-300"
            style={{
              width: `${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}%`,
              background: 'var(--besty-blue)', // matches RA
            }}
          />
        </div>
      </div>

      {/* Completion Banner */}
      {isAllCompleted && (
        <Banner type="success">{t("You're all set! All required tasks have been completed for your stay.")}</Banner>
      )}

      {/* Tasks Section */}
      {items.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">{t('No actions required for your stay.')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sequenceData.order.map((key) => {
            const item = itemsByKey[key];
            if (!item) return null;

            return (
              <div
                key={item.key}
                role="button"
                tabIndex={0}
                className="cursor-pointer"
                onClick={() => handleTaskClick(item.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTaskClick(item.key);
                  }
                }}
              >
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <h2 className="text-base font-semibold text-gray-900">{getTaskTitle(item.key, t)}</h2>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium gap-1.5 ${
                            item.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : item.status === 'in_progress'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {item.status === 'completed' && (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                          {item.status === 'in_progress' && (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                          {item.status === 'not_started' && (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                          {item.status === 'completed'
                            ? t('Complete')
                            : item.status === 'in_progress'
                              ? t('In Progress')
                              : t('Not Started')}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">{getTaskDescription(item.key, t)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function getTaskTitle(key: string, t: (key: string) => string): string {
  switch (key) {
    case 'rental_agreement':
      return t('Rental Agreement');
    case 'damage_waiver':
      return t('Damage Waiver');
    case 'check_in_form':
      return t('Check-in Form');
    default:
      return t('Task');
  }
}

// Task descriptions (static content)
function getTaskDescription(key: string, t: (key: string) => string): string {
  switch (key) {
    case 'rental_agreement':
      return t('Review and sign your rental agreement for this stay.');
    case 'damage_waiver':
      return t('Choose your preferred option to protect against accidental damages during your stay.');
    case 'check_in_form':
      return t('Provide your basic details to speed up your arrival.');
    default:
      return t('Complete this required task.');
  }
}

export default Sequence;
